import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useMemo, useState, useEffect, type ReactNode } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { RoavaCard, StatusChip, SecondaryButton } from "@/components/roava";
import { PageHeader } from "@/components/admin/PageHeader";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { getMyTrip } from "@/lib/trip.functions";
import {
  listAdminPolicies,
  setAdminPolicy,
  listRecentDispatchLog,
  type AdminPolicyRow,
} from "@/lib/admin-notifications.functions";
import { NOTIF_CATEGORIES, type NotifCategory } from "@/lib/notifications.types";
import {
  getLocalNotifDebug,
  resetLocalNotifCooldowns,
} from "@/lib/native/local-notifications-scheduler";
import { debugCheckQueueStuckNow } from "@/lib/native/notification-watchers";
import { AdminAlertsPanel } from "@/components/admin/AdminAlertsPanel";
import { SweepScheduleCard } from "@/components/admin/SweepScheduleCard";



export const Route = createFileRoute("/_authenticated/admin/notifications")({
  head: () =>
    pageMeta({
      title: "Notification Controls — Roava by WGP Apps",
      description: "Choose which notifications travelers can receive for this trip.",
      path: "/admin/notifications",
    }),
  component: AdminNotifications,
});

const STATUS_TONES: Record<string, "success" | "warning" | "neutral" | "danger"> = {
  sent: "success",
  suppressed_admin: "warning",
  suppressed_user: "neutral",
  suppressed_mute: "neutral",
  suppressed_quiet: "neutral",
  suppressed_dedupe: "neutral",
  no_device: "warning",
  error: "danger",
};

function AdminNotifications() {
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const listFn = useServerFn(listAdminPolicies);
  const setFn = useServerFn(setAdminPolicy);
  const logFn = useServerFn(listRecentDispatchLog);

  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id ?? null;
  const tripName = tripData?.trip?.name ?? "your trip";

  const { data: policies = [] } = useQuery({
    queryKey: ["admin-notif-policies"],
    queryFn: () => listFn(),
  });

  const { data: log = [] } = useQuery({
    queryKey: ["admin-notif-log"],
    queryFn: () => logFn(20),
    refetchInterval: 30_000,
  });

  const byScope = useMemo(() => {
    const global: Record<string, AdminPolicyRow> = {};
    const trip: Record<string, AdminPolicyRow> = {};
    for (const p of policies) {
      if (p.trip_id === null) global[p.category] = p;
      else if (p.trip_id === tripId) trip[p.category] = p;
    }
    return { global, trip };
  }, [policies, tripId]);

  const mut = useMutation({
    mutationFn: setFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-notif-policies"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const setEnabled = (
    scope: "global" | "trip",
    category: NotifCategory,
    enabled: boolean,
  ) => {
    if (scope === "trip" && !tripId) return;
    mut.mutate({
      trip_id: scope === "global" ? null : tripId,
      category,
      enabled,
    });
  };

  // Effective resolution: trip row wins, then global, then default true.
  const effective = (category: NotifCategory): boolean => {
    if (tripId && byScope.trip[category]) return byScope.trip[category].enabled;
    if (byScope.global[category]) return byScope.global[category].enabled;
    return true;
  };

  const tripOverrideCount = Object.keys(byScope.trip).length;
  const lastActivity = log[0]?.sent_at ?? null;
  const [localDebugCount, setLocalDebugCount] = useState(() => getLocalNotifDebug().length);
  useEffect(() => {
    const t = setInterval(() => setLocalDebugCount(getLocalNotifDebug().length), 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <main className="mx-auto max-w-4xl px-5 md:px-8 py-6 md:py-8">
      <PageHeader
        title="Notifications"
        subtitle={`Decide which pushes travelers can receive for ${tripName}.`}
      />

      <RoavaCard className="mb-4 p-5">
        <p className="text-[13px] text-[var(--roava-text-muted)] leading-relaxed">
          Each row is a category. Turning a category off here suppresses every
          push of that type for every traveler — overriding their personal
          settings. <strong>Trip override</strong> applies only to{" "}
          <em>{tripName}</em>. <strong>Global</strong> applies everywhere unless
          a trip row says otherwise.
        </p>
      </RoavaCard>

      <Accordion type="multiple" defaultValue={["traveler"]} className="space-y-3">
        <Section
          value="traveler"
          title="Traveler categories"
          summary={`${NOTIF_CATEGORIES.length} categories${tripOverrideCount ? ` · ${tripOverrideCount} overridden for this trip` : ""}`}
        >
          <RoavaCard flush className="overflow-hidden">
            <div className="grid grid-cols-[minmax(0,1fr)_64px_64px_56px] sm:grid-cols-[minmax(0,1fr)_120px_120px_90px] px-3 sm:px-4 py-3 text-[11px] uppercase tracking-[0.1em] text-[var(--roava-text-muted)] border-b border-[var(--roava-border)]">
              <span>Category</span>
              <span className="text-center">Global</span>
              <span className="text-center">
                <span className="sm:hidden">Trip</span>
                <span className="hidden sm:inline">This trip</span>
              </span>
              <span className="text-center">
                <span className="sm:hidden">Eff.</span>
                <span className="hidden sm:inline">Effective</span>
              </span>
            </div>
            {NOTIF_CATEGORIES.map((cat) => {
              const g = byScope.global[cat.key];
              const t = byScope.trip[cat.key];
              const gEnabled = g ? g.enabled : true;
              const tEnabled = t ? t.enabled : gEnabled;
              const eff = effective(cat.key);
              return (
                <div
                  key={cat.key}
                  className="grid grid-cols-[minmax(0,1fr)_64px_64px_56px] sm:grid-cols-[minmax(0,1fr)_120px_120px_90px] items-center px-3 sm:px-4 py-4 border-b border-[var(--roava-border)] last:border-0"
                >
                  <div className="min-w-0 pr-2 sm:pr-4">
                    <p className="text-[14px] font-medium text-[var(--roava-text)] break-words">
                      {cat.label}
                    </p>
                    <p className="text-[12px] text-[var(--roava-text-muted)] mt-0.5 break-words">
                      {cat.description}
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={gEnabled}
                      onCheckedChange={(v) => setEnabled("global", cat.key, v)}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={tEnabled}
                      disabled={!tripId}
                      onCheckedChange={(v) => setEnabled("trip", cat.key, v)}
                    />
                  </div>
                  <div className="flex justify-center">
                    <StatusChip variant={eff ? "success" : "danger"}>
                      {eff ? "On" : "Off"}
                    </StatusChip>
                  </div>
                </div>
              );
            })}
          </RoavaCard>
        </Section>

        <Section
          value="activity"
          title="Recent activity (your account)"
          summary={lastActivity ? `${log.length} recent · last ${new Date(lastActivity).toLocaleString()}` : "Nothing dispatched yet"}
        >
          <RoavaCard flush className="overflow-hidden">
            {log.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[var(--roava-text-muted)]">
                No notifications dispatched yet.
              </div>
            ) : (
              log.map((row) => (
                <div
                  key={row.id}
                  className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-2.5 border-b border-[var(--roava-border)] last:border-0 text-[12.5px]"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--roava-text)] truncate">
                      {row.category}
                    </p>
                    <p className="text-[11px] text-[var(--roava-text-muted)] tabular">
                      {new Date(row.sent_at).toLocaleString()}
                    </p>
                  </div>
                  <StatusChip variant={STATUS_TONES[row.status] ?? "neutral"}>
                    {row.status}
                  </StatusChip>
                  <span className="text-[11px] tabular text-[var(--roava-text-muted)] min-w-[40px] text-right">
                    {row.apns_status ?? "—"}
                  </span>
                </div>
              ))
            )}
          </RoavaCard>
        </Section>

        <Section
          value="local-debug"
          title="Local notifications — debug"
          summary={`On-device reminders · ${localDebugCount} decision${localDebugCount === 1 ? "" : "s"} logged`}
        >
          <LocalNotifDebugPanel />
        </Section>

        <Section
          value="sweep"
          title="Sweep schedule"
          summary="How often the system auto-checks for backlog, errors, and stale data"
        >
          <SweepScheduleCard />
        </Section>

        <Section
          value="admin-alerts"
          title="Admin-only alerts"
          summary="Owner ops · ping me when conditions trigger"
        >
          <AdminAlertsPanel />
        </Section>
      </Accordion>
    </main>
  );
}

function Section({ value, title, summary, children }: { value: string; title: string; summary?: string; children: ReactNode }) {
  return (
    <AccordionItem value={value} className="border-0">
      <RoavaCard className="overflow-hidden p-0">
        <AccordionTrigger className="px-4 sm:px-5 py-4 hover:no-underline">
          <div className="flex flex-col items-start gap-0.5 min-w-0 pr-3">
            <span className="text-[15px] font-semibold tracking-tight text-[var(--roava-text)]">{title}</span>
            {summary && <span className="text-[12px] font-normal text-[var(--roava-text-muted)] text-left">{summary}</span>}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 sm:px-5 pt-0 pb-5">
          {children}
        </AccordionContent>
      </RoavaCard>
    </AccordionItem>
  );
}


const OUTCOME_TONES: Record<string, "success" | "warning" | "neutral" | "danger"> = {
  scheduled: "success",
  skipped_web: "neutral",
  skipped_category: "neutral",
  skipped_quiet: "neutral",
  skipped_cooldown: "neutral",
  skipped_no_permission: "warning",
  skipped_same_fingerprint: "neutral",
  error: "danger",
};

function LocalNotifDebugPanel() {
  const [entries, setEntries] = useState(() => getLocalNotifDebug());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setEntries(getLocalNotifDebug()), 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <p className="text-[12px] text-[var(--roava-text-muted)] max-w-md">
          Shows why on-device reminders fired or were skipped on this device.
          Use the buttons to force a check or wipe rate limits if a reminder
          isn't appearing during testing.
        </p>

        <div className="flex gap-2">
          <SecondaryButton
            size="sm"
            onClick={async () => {
              setBusy(true);
              const before = getLocalNotifDebug().length;
              try {
                await debugCheckQueueStuckNow();
                const after = getLocalNotifDebug();
                setEntries(after);
                const added = after.length - before;
                toast.success(
                  added > 0
                    ? `Queue check ran — ${added} new decision${added === 1 ? "" : "s"} logged below.`
                    : "Queue check ran — nothing to report. Upload queue looks healthy.",
                );
              } catch (e) {
                toast.error(String(e));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            Run queue check
          </SecondaryButton>
          <SecondaryButton
            size="sm"
            onClick={async () => {
              await resetLocalNotifCooldowns();
              setEntries(getLocalNotifDebug());
              toast.success("Cooldowns cleared — the next reminder can fire immediately.");
            }}
          >
            Reset cooldowns
          </SecondaryButton>
        </div>
      </div>

      <RoavaCard flush className="overflow-hidden">
        {entries.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--roava-text-muted)]">
            No local-notification decisions yet. Open Roava on iOS, start a trip,
            or tap "Run queue check" to generate one.
          </div>
        ) : (
          entries.map((e, i) => (
            <div
              key={`${e.at}-${i}`}
              className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-2.5 border-b border-[var(--roava-border)] last:border-0 text-[12.5px]"
            >
              <div className="min-w-0">
                <p className="font-medium text-[var(--roava-text)] truncate">
                  {e.kind}
                </p>
                <p className="text-[11px] text-[var(--roava-text-muted)] tabular truncate">
                  {new Date(e.at).toLocaleString()}
                  {e.reason ? ` · ${e.reason}` : ""}
                  {e.extra ? ` · ${JSON.stringify(e.extra)}` : ""}
                </p>
              </div>
              <StatusChip variant={OUTCOME_TONES[e.outcome] ?? "neutral"}>
                {e.outcome}
              </StatusChip>
            </div>
          ))
        )}
      </RoavaCard>
    </div>
  );
}
